import React, { useContext, useEffect, useState } from "react";
import { Gantt, Task, ViewMode } from "gantt-task-react";
import "gantt-task-react/dist/index.css";
import CustomTaskListHeader from "./CustomTaskListHeader";
import CustomTaskListTable from "./CustomTaskListTable";
import CustomTooltip from "./CustomTooltip";
import { ISort } from "../../pages/Project/Project";
import { Context } from "../../index";
import TaskAddEditForm from "../Task/TaskAddEditForm/TaskAddEditForm";
import { IGetTaskFilter } from "../../common/api_interfaces/task/IGetTaskFilter";
import { IGetTasksDisplayData } from "../../common/api_interfaces/task/IGetTasksDisplayData.request";
import { ITaskDisplay } from "../../common/api_interfaces/task/ITaskDisplay.response";

// Расширяем Task, добавляя кастомные поля
interface ExtendedTask extends Task {
    taskId?: number;
}

interface IGanttChartProps {
    projectId: number;
    projectStartDate?: Date;
    boardId?: number;
    responsibleRequiredOnCreate: boolean;
}

const GanttChart: React.FC<IGanttChartProps> = ({ projectId, boardId, responsibleRequiredOnCreate, projectStartDate }) => {
    const { store } = useContext(Context);
    const [tasks, setTasks] = useState<ExtendedTask[]>([]);
    const [taskId, setTaskId] = useState<number>();
    const [hidden, setHidden] = useState<boolean>(false);

    const parseDate = (dateString: string | Date | null | undefined, dateName?: "start" | "end"): Date => {
        let newDate = projectStartDate ? new Date(projectStartDate) : new Date();
        if (!dateString) {
            if (dateName === "start") {
                // Если это end и дата не задана — завтрашний день
                return new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), 0, 0);
            }
            if (dateName === "end") {
                // Если это end и дата не задана — завтрашний день
                return new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), 23, 59);
            }
            // Если start и не задано — сегодня
            return newDate;
        }
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? newDate : date;
    };

    useEffect(() => {
        fetchTasks();
    }, [boardId, projectId]);

    const fetchTasks = async () => {
        const filters: IGetTaskFilter = {
            projectId: projectId,
            date: undefined,
            name: undefined,
            priority: undefined,
            responsible: undefined,
            status: undefined,
            taskId: undefined,
            typeTask: undefined,
            boardIds: boardId ? [boardId] : [],
        };
        const sort: ISort = {
            date: "asc",
        };
        const newPageReq: IGetTasksDisplayData = {
            skip: 0,
            take: 2000,
            filters,
            sort,
        };
        try {
            const response = await store.taskService.getProjectTasksDisplayData(newPageReq);
            const parsedTasks: ExtendedTask[] = response.data.tasks
                .filter((task): task is ITaskDisplay => !!task)
                .map((task: ITaskDisplay) => {
                    const hasStart = !!task.startDate;
                    const hasEnd = !!task.endDate;

                    let startDate: Date;
                    let endDate: Date;

                    if (hasStart) {
                        startDate = parseDate(task.startDate, "start");
                    } else if (hasEnd) {
                        // Если нет начала — используем день до окончания
                        const dayBefore = new Date(task.endDate!);
                        dayBefore.setDate(dayBefore.getDate() - 1);
                        startDate = parseDate(dayBefore, "start");
                    } else {
                        startDate = parseDate(undefined, "start");
                    }

                    if (hasEnd) {
                        endDate = parseDate(task.endDate, "end");
                    } else if (hasStart) {
                        const dayAfter = new Date(task.startDate!);
                        dayAfter.setDate(dayAfter.getDate() + 1);
                        endDate = parseDate(dayAfter, "end");
                    } else {
                        endDate = parseDate(undefined, "end");
                    }

                    return {
                        taskId: task.id,
                        id: task.idForView || `task-${task.id}`,
                        type: "task",
                        name: task.name || "Без названия",
                        start: startDate,
                        end: endDate,
                        progress: task.progress ?? 0,
                        isDisabled: false,
                        project: task.columns.main?.name,
                    };
                });
            setTasks(parsedTasks);
        } catch (error) {
            console.error("Ошибка при загрузке задач", error);
        }
    };

    const handleDateChange = (task: ExtendedTask) => {
        const newTasks = tasks.map((t) => (t.id === task.id ? task : t));
        setTasks(newTasks);
        updateTaskDates(task);
    };

    const updateTaskDates = async (task: ExtendedTask) => {
        await store.taskService.updateTaskDates({
            projectId: projectId,
            id: task.taskId!,
            endDate: task.end,
            startDate: task.start,
        });
    };

    const handleClick = (task: ExtendedTask) => {
        setTaskId(task.taskId);
        setHidden(true);
    };

    const onTaskAction = () => {
        setHidden(false);
        fetchTasks();
    };

    if (tasks.length === 0) {
        return <div>Нет задач для этой доски</div>;
    }

    return (
        <div className="gant_render--container">
            <Gantt
                tasks={tasks}
                viewMode={ViewMode.Day}
                TaskListHeader={CustomTaskListHeader}
                TaskListTable={CustomTaskListTable}
                TooltipContent={CustomTooltip}
                locale="ru"
                onDateChange={handleDateChange}
                onDoubleClick={handleClick}
            />
            {hidden && (
                <div className="full-screen-overlay">
                    <div className="popup-form-background" onClick={(e) => e.stopPropagation()}>
                        <TaskAddEditForm
                            responsibleRequired={responsibleRequiredOnCreate}
                            mode="edit"
                            taskId={taskId}
                            closeFunc={() => setHidden(false)}
                            projectId={projectId}
                            onTaskAction={onTaskAction}
                            optionsQueryParams={{
                                page: "project",
                                entity: "task-form",
                                action: "create",
                                entityId: undefined,
                                cheklistId: undefined,
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default GanttChart;
